import 'styled-components';

const geyserBlue = '#D0DADF';
const daintreeBlue = '#002A3C';
const sweetCorn = '#fcf090';
const onahau = '#CBF0FF';
const cerulean = '#007499';
const prussianBlue = '#004866';
const richElectricBlue = '#0199d8';
const mystic = '#E4ECEF';
const fiord = '#385461';
const white = '#ffffff';
const offWhite = '#F6F5F3';
const timGray = '#00242c';
const timRed = '#ac1d00';
const backgroundGreen = '#E2FBDC';
const deepGreen = '#21850C';
const linen = '#F9EEE7';
const greyishBlue = '#9DB3BC';
const platinum = '#E5E5E5';
const lightBlue = '#E9F1F5';
const lightGrey = '#C2C2C2';

// new learner design colours
const purple = '#9381A5';
const darkPurple = '#5E4F6E';
const purpleGradientLight = `linear-gradient(90deg, #efe8f5 0%, #f8f3fc 100%)`;
const purpleGradientDark = `linear-gradient(90deg, #ac97bf 0%, #cdb3e3 100%)`;
const lilacGradient = `linear-gradient(90deg, #f0f2ff 0%, #cdd3f1 100%)`;

// header gradients
const gradientLearner = `linear-gradient(90deg, #a591b9 0%, #ad99c3 33%, #b2a3c0 66%, #b6a6c6 100%)`;
const gradientTrainer = `linear-gradient(90deg, #08799d 0%, #3391b0 30%, #3391b0 45%, #4aa3c2 70%, #62a7ce 100%)`;
const gradientOrg = `linear-gradient(90deg, #528c96 0%, #61a0a9 35%, #61a0a9 50%, #56b0ba 75%, #4bb9c2 100%)`;

// header text background colours
const textBackgroundLearner = '#5E4F6E';
const textBackgroundTrainer = '#1C4764';
const textBackgroundOrg = '#185d63';

// https://www.figma.com/file/EEnbtBmjVv8tesa8utz2DP/Neve-Design-System?node-id=1501%3A1300
// this theme should mimic the designs in this document as closely as possible
export const theme = {
    fontFamilyHeader: `"Work Sans", serif`,
    fontFamilyMain: `"Open Sans", serif`,

    // brand colours
    primaryBrandColour: cerulean,
    secondaryBrandColour: sweetCorn,

    // text
    textColour: timGray,
    textColourInverted: white,
    textSecondaryColour: fiord,

    // positive and negative messaging
    errorColour: timRed,
    successColour: deepGreen,

    //BUTTONS
    buttonFocusTextColour: cerulean,
    buttonDisabledBackgroundColour: geyserBlue,
    buttonFocusBackgroundColour: onahau,
    // primary button
    primaryButtonTextColour: white,
    primaryButtonBackgroundColour: prussianBlue,
    primaryButtonBorderColour: richElectricBlue,
    primaryButtonHoverBackgroundColour: cerulean,
    // secondary button
    secondaryButtonTextColour: cerulean,
    secondaryButtonBackgroundColour: white,
    secondaryButtonBackgroundSelectedColour: greyishBlue, //remove
    secondaryButtonBorderColour: richElectricBlue,
    secondaryButtonHoverBackgroundColour: mystic,
    secondaryButtonDisabledBackgroundColour: geyserBlue, //remove
    secondaryButtonIconColor: daintreeBlue,
    //black buttons
    blackButtonTextColour: timGray,
    blackButtonBorderColour: timGray,

    // course editor
    courseEditButtonBackgroundColour: lightBlue,
    courseEditHintBackgroundColour: lightBlue,

    // links
    linkColour: prussianBlue,
    linkFocusBorderColour: richElectricBlue,
    linkFocusBackgroundColour: onahau,
    linkHoverBackgroundColour: mystic,
    linkVisitedColour: prussianBlue,

    // neutrals
    neutralZero: daintreeBlue,
    neutralOne: fiord,
    neutralFour: platinum,

    // inputs
    inputBorderColour: fiord,
    inputActiveColour: cerulean,
    inputBackgroundError: white,
    inputRadioButton: {
        borderColour: fiord,
        activeRadioColour: cerulean,
        focusBackgroundColour: onahau,
        hoverBackgroundColour: platinum,
        checkedBorderColour: richElectricBlue,
    },

    // backgrounds
    appBackgroundColour: offWhite,
    contentBackgroundColourTrainer: offWhite,
    contentBackgroundColourLearner: offWhite,
    footerBackground: prussianBlue,
    learnerFooterBackground: darkPurple,
    organisationTrainerBackgroundColour: white,
    learnerCourseBackgroundColour: lightBlue,

    // GENERIC COMPONENTS

    // notifications
    notificationSuccessBackground: backgroundGreen,
    notificationSuccessBorder: deepGreen,
    notificationWarningBackground: sweetCorn,
    notificationWarningBorder: daintreeBlue,
    notificationDangerBackground: linen,
    notificationDangerBorder: timRed,
    notificationInfoBackground: platinum,
    notificationInfoBorder: fiord,
    notificationAccessibilityBackground: lightBlue,
    notificationAccessibilityBorder: fiord,

    // tabs
    inactiveTab: daintreeBlue,
    activeTab: cerulean,
    learnerTabBackground: lilacGradient,

    // banners
    adminHeader: greyishBlue,
    learnerHeader: gradientLearner,
    trainerHeader: gradientTrainer,
    orgHeader: gradientOrg,
    adminSubpageHeaderColour: mystic,

    // banner text backgrounds
    learnerHeaderTextBackground: textBackgroundLearner,
    trainerHeaderTextBackground: textBackgroundTrainer,
    orgHeaderTextBackground: textBackgroundOrg,

    // cards
    cardBackground: white,
    cardSecondaryBackgroundColour: offWhite,
    cardThirdBackgroundColour: onahau,
    cardBorder: geyserBlue,
    cardSecondaryBorder: fiord,
    iconBackgroundColour: geyserBlue,

    // navigation bar
    navigationBar: {
        background: white,
        buttonFocusBackground: onahau,
        buttonFocusBorder: cerulean,
        buttonFocusText: cerulean,
        buttonText: daintreeBlue,
        buttonHoverBackground: mystic,
        border: fiord,
        divider: geyserBlue,
    },
    cardNavigation: {
        borderFocused: richElectricBlue,
        backgroundFocused: onahau,
        backgroundHover: platinum,
        heading: cerulean,
        headingHover: prussianBlue,
    },
    answersProgressBar: {
        backgroundTotal: white,
        backgroundAnswered: deepGreen,
        borderTotal: geyserBlue,
    },
    learnerActivityCard: {
        background: purpleGradientLight,
        backgroundHover: purpleGradientDark,
        completedBackground: backgroundGreen,
        completedNotificationBackground: deepGreen,
        iconCircleBackground: cerulean,
    },
    circularProgressBar: {
        darkVersionIndicatorColour: deepGreen,
        darkVersionIndicatorTrackColour: white,
        darkVersionBackgroundColour: daintreeBlue,
        darkVersionTextColour: white,
        lightVersionBackground: geyserBlue,
        lightVersionFilledColour: deepGreen,
    },

    // stepper
    stepperBackgroundColour: daintreeBlue,
    stepperTextColour: white,
    stepperIncompleteLineColour: fiord,
    stepperCompleteLineColour: white,

    // accessibility panel
    accessibilityPanel: {
        buttonBackgroundColour: cerulean,
        textColour: white,
        buttonBorder: geyserBlue,
        buttonFocusBorder: richElectricBlue,
        butonFocusBackgroundColour: cerulean,
        backgroundColour: daintreeBlue,
        openPanelButtonBackground: sweetCorn,
    },
    spinnerColour: prussianBlue,
    roleTagBackgroundColour: linen,
    tableHeaderBackgroundColour: platinum,
    progressBar: {
        foreground: prussianBlue,
        background: mystic,
    },
    stepCheck: {
        circleColourIncomplete: lightGrey,
        circleColour: prussianBlue,
        checkColour: white,
    },
    aiLogoColour: purple,

    runningLiveCourse: backgroundGreen,

    // colours
    mystic,
    fiord,
};

export const sizes = {
    appMaxWidth: 1440,
    appNavWidth: 1250,
    appMaxWidthWithoutPadding: 1270,
    learnerImmersiveContentMaxWidth: '1000px',
    learnerCourseContentMaxWidth: '900px',
    edgePadding: '15px',
    borderRadiusMd: '4px',
    borderRadiusLg: '8px',
    borderRadiusXl: '12px',
    headerHeight: '80px',
    headerMobileHeight: '130px',
    headerMobileHeightSlim: '80px',
    footerHeight: '120px',
    learnerTabsHeight: '80px',
    spacingXs: '4px',
    spacingSm: '8px',
    spacingMd: '16px',
    spacingRg: '24px',
    spacingLg: '32px',
    spacingXl: '64px',
    rem: {
        md: '1.75rem',
        rg: '1.25rem',
        sm: '0.5rem',
        xs: '0.25rem',
    },
};

export const breakpoints = {
    xs: '375px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
};

export enum FONTSIZE {
    Headline1 = '3rem', // 48px
    Headline2 = '2.25rem', // 36px
    Headline3 = '1.75rem', // 28px
    Headline4 = '1.25rem', // 20px
    Headline5 = '1.125rem', // 18px
    Headline6 = '1rem', // 16px

    BodyLarge = '1.25rem', // 20px
    BodyRegular = '1.125rem', // 18px
    BodySmall = '1rem', // 16px
}
